// WU-2.4 T6 — /api/queue-evictions + /api/admin/queue-evictions client.
// Paths WITHOUT the /api prefix (@/lib/api-client adds it). Shapes mirror
// routes/admin/queue-evictions/index.ts exactly.
import { api } from "@/lib/api-client";

export interface MyEviction {
  id: string;
  stepType: string;
  jobTypeId: string | null;
  mode: "immediate" | "after_document" | "after_batch";
  reason: string;
  expiresAt: string | null;
}

/**
 * WU-2.4 T7 — the single operator-facing eviction notice, shared by every
 * surface that renders one (the evicted-lane lock in StepSection, the
 * refused-claim toast on a lane claim, the VS immediate-exit toast) so the
 * copy can't drift between them.
 */
export function formatEvictionNotice(reason: string): string {
  return `You've been moved off this queue — ${reason}`;
}

/**
 * WU-2.4 T8 — the single decision for "was this claim refusal caused by an
 * eviction, and what do we tell the operator?". `/queues/next` refuses an
 * evicted lane with `{ available: false, reason: "evicted", evictionReason }`
 * (T7); both claim paths (the dashboard's per-lane claim fallback and the
 * VS's inline continuous-feed re-claim) must recognize it identically.
 * Returns the formatted notice, or null when the refusal wasn't an eviction.
 */
export function evictionRefusalNotice(resp: {
  reason?: string;
  evictionReason?: string;
}): string | null {
  return resp.reason === "evicted" ? formatEvictionNotice(resp.evictionReason ?? "") : null;
}

/**
 * WU-2.4 — payload of the `queue_eviction` WS event the server publishes on
 * the operator's PRIVATE user channel (apps/api/src/services/
 * queue-eviction.ts publishEviction). This is the UNWRAPPED payload
 * (WsClient.onPayload strips the `{ type, payload }` wire envelope). The one
 * canonical declaration — consumers (useMyEvictions, the VS eviction exit)
 * derive from it rather than redeclaring the shape.
 */
export interface QueueEvictionEvent {
  evictionId: string;
  stepType: string;
  jobTypeId: string | null;
  mode: MyEviction["mode"];
  reason: string;
}

// Bare row as the server serializes it (services/queue-eviction.ts
// EvictionRecord) — what create/clear return.
export interface EvictionRecord extends MyEviction {
  tenantId: string;
  userId: string;
  graceBatchId: string | null;
  createdBy: string;
  createdAt: string;
  clearedBy: string | null;
  clearedAt: string | null;
}

// Only the LIST route resolves display names (resolveNames, with a
// userId fallback — always strings); create/clear return the bare record.
export interface AdminEviction extends EvictionRecord {
  userName: string;
  createdByName: string;
}

export interface CreateEvictionBody {
  userId: string;
  stepType: string;
  jobTypeId?: string;
  mode: MyEviction["mode"];
  reason: string;
  expiresAt?: string;
}

export const evictionsApi = {
  mine: () => api.get<{ evictions: MyEviction[] }>("/queue-evictions/mine"),
  create: (body: CreateEvictionBody) =>
    api.post<{ eviction: EvictionRecord }>("/admin/queue-evictions", body),
  list: () => api.get<{ evictions: AdminEviction[] }>("/admin/queue-evictions"),
  clear: (id: string) =>
    api.post<{ eviction: EvictionRecord }>(`/admin/queue-evictions/${id}/clear`, {}),
};
